import { store, useAppSelector } from 'app';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { useCallback, useEffect, useState } from 'react';
import CustomerAPI, { GravityPaymentInvoicesData, GravityPaymentInvoicesResponse, TextToPayLinkRequest, TextToPayLinkResponse } from 'components/customer/api/CustomerAPI';
import { Conversation } from 'components/messaging/models/conversation';
import AppSnackbar from "components/snackbar/snackbar";
import { AppBar, Box, CircularProgress, Container, Grid, Button, IconButton, MenuItem, Popover, Select, TextField, Toolbar, Tooltip, Typography, makeStyles, Tabs, Tab, FormControl, RadioGroup, Radio, FormControlLabel, InputAdornment } from '@material-ui/core';
import { Console } from 'console';
import CloseIcon from '@material-ui/icons/Close';
import { Padding } from '@mui/icons-material';
import moment from 'moment';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import { MemoMessageBodyLimit, MessagePaymentStatus, MessageTypeConst, OrderType } from 'common/utils/shared/shared-constants';

import { GravityPaymentMessageBody } from 'components/messaging/api/messaging-api';
import { BaseResponse } from 'common/utils/shared/shared-models';

const useStyles = makeStyles((theme) => ({
  select: {
    display: 'flex',
    alignItems: 'center',
  },
  toolbar: {
    backgroundColor: "#3f51b5",
    minHeight: 50,
    paddingLeft: 8,
    paddingRight: 8,
  },
  title: {
    color: "white",
    fontWeight: "bold",
  },
  tabs: {
    minHeight: 36,
    borderBottom: "1px solid #e0e0e0",
  },
  tab: {
    fontSize: "0.75rem",
    minWidth: 80,
    minHeight: 36,
    padding: "4px 8px",
  },
  contentBox: {
    minHeight: 275,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
  },
  fieldsWrapper: {
    padding: 16,
    paddingTop: 20,
  },
  footer: {
    borderTop: "1px solid #e0e0e0",
    padding: "12px 16px",
    textAlign: "right",
    background: "#fafafa",
  },
  footerButton: {
    minWidth: 180,
  },
  tabRoot: {
    fontSize: "0.75rem",
    minWidth: 80,
    minHeight: 36,
    padding: "4px 8px",
  },
}));

function TextToPay({ Conversation, SendPayment, isShowInvoices }: { Conversation: Conversation; SendPayment: any; isShowInvoices: boolean; }) {
  const classes = useStyles();
  const dmsDealerId = useAppSelector((state) => state.user.dmsDealerId);
  const [anchorEl, setAnchorEl] = useState<any>(null);
  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;
  const [isSendingMessage, SetIsSendingMessage] = useState<boolean>(false);
  const [memo, setMemo] = useState<string>('');
  const [showSnackBar] = AppSnackbar.useSnackBar();
  const [amount, setAmount] = useState<string>('');
  const [selectedInvoice, setSelectedInvoice] = useState<number | undefined>(undefined);
  const [gravityInvoices, setGravityInvoices] = useState<GravityPaymentInvoicesResponse | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isNoInvoices, setIsNoInvoices] = useState<boolean>(false);
  const [tab, setTab] = useState<number>(0);
  const [mode, setMode] = useState<'amount' | 'percentage'>('amount');
  const [percentage, setPercentage] = useState<number | null>(null);
  const [selectedInvoiceAmount, setSelectedInvoiceAmount] = useState<number | null>(null);

  const isTesting = () => {
    const allowedDealer = [999002, 999302, 99204001, 99502];
    return allowedDealer.includes(dmsDealerId) || isShowInvoices;
  };

  const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setTab(newValue);
    resetForm();
  };

  const handlePercentageChange = (value: string) => {
    if (!selectedInvoice || !gravityInvoices) return;

    const invoice = gravityInvoices.find(x => x.invoiceId === selectedInvoice);
    if (!invoice) return;

    const percentageValue = parseFloat(value);

    if (isNaN(percentageValue) || percentageValue < 0) {
      setPercentage(null);
      setAmount("");
      return;
    }

    // invoice balance left = total - already paid
    const balance = (invoice.invoiceAmount ?? 0) - (invoice.payment ?? 0);

    const calculatedAmount = (balance * percentageValue) / 100;

    setPercentage(percentageValue);
    setAmount(calculatedAmount.toFixed(2)); // show as 2 decimal places
  };


  useEffect(() => {
    const invoice = gravityInvoices?.find(x => x.invoiceId === selectedInvoice as number);
    setPercentage(null);
    if (selectedInvoice && invoice?.invoiceAmount) {
      if (tab === 0) {
        setAmount(invoice?.invoiceAmount?.toString());
      } else {
        setAmount('');
      }
    }
    // keep existing dependencies (mode, tab) as you had it
  }, [tab]);

  // ------------------- Clear state if invoice is removed -------------------
  useEffect(() => {
    if (!gravityInvoices || !selectedInvoice) return;

    const stillExists = gravityInvoices.some(x => x.invoiceId === selectedInvoice);

    if (!stillExists) {
      // Clear selection and amount if invoice is gone
      setSelectedInvoice(undefined);
      setSelectedInvoiceAmount(null);
      setAmount("");
      setPercentage(null);
    }
  }, [gravityInvoices, selectedInvoice]);


  const disableBtn = () => {
    if (tab === 0 || tab === 1) {
      const invoice = gravityInvoices?.find(x => x.invoiceId === (selectedInvoice as number));
      return (
        amount === '' ||
        parseFloat(amount || '0') === 0 ||
        (isTesting() && !selectedInvoice) ||
        isSendingMessage ||
        (isTesting() && (invoice?.invoiceAmount ? parseFloat(amount) > invoice?.invoiceAmount : true))
      );
    }

    if (tab === 2) {
      return (
        amount === '' ||
        isSendingMessage ||
        parseFloat(amount || '0') < 0.01 ||
        parseFloat(amount || '0') > 1000
      );
    }

    return true;
  };

  useEffect(() => {
    if (open && isTesting()) {
      const fetchGravityInvoices = async () => {
        try {
          setIsLoading(true);

          if (!Conversation.invoiceCustomerId || Conversation.invoiceCustomerId < 0 ||
            !Conversation.dealerId || Conversation.dealerId < 0) {

            setIsNoInvoices(true);
            setIsLoading(false);
            return;
          }

          const OrderTypeList = [OrderType.SaleOrder, OrderType.WorkOrder].join(",");
          let response = await CustomerAPI.GetCustomerInvoices({
            queryBody: {
              DealerId: Conversation.dealerId,
              CustomerId: Conversation.invoiceCustomerId?.toString(),
              OrderType: OrderTypeList
            },
          });
          if (response.length > 0) {
            setGravityInvoices(response);
            setIsNoInvoices(false);
          } else {
            setIsNoInvoices(true);
          }
          setIsLoading(false);
        } catch {
          setGravityInvoices(undefined);
          setIsNoInvoices(true);
          setIsLoading(false);
        }
      };
      fetchGravityInvoices();
    }
  }, [open, Conversation]);

  const handleClick = (event: any): void => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = (): void => {
    setAnchorEl(null);
    resetForm();
    setTab(0);
  };
  const resetForm = () => {
    setMemo('');
    setAmount('');
    setSelectedInvoice(undefined);
    setSelectedInvoiceAmount(null);

    setPercentage(null);
    setMode('amount');
  };

  async function SendPaymentRequest() {
    try {
      SetIsSendingMessage(true);

      const linkRequest: TextToPayLinkRequest = {
        dealerId: Conversation.dealerId ?? store.getState().user.dealerId,
        crmCustomerId: Conversation.customer.crmCustomerId,
        customerName: Conversation.customer.name,
        departmentName: Conversation.departmentName,
        memo: memo,
        amount: amount,
        invoiceId: selectedInvoice?.toString() ?? "",
        orderId: gravityInvoices?.find(x => x.invoiceId === selectedInvoice)?.orderId
      };

      const response: BaseResponse<TextToPayLinkResponse> = await CustomerAPI.fetchTextToPayLink(linkRequest);
      if (!response.isSuccess) {
        handleClose();
        SetIsSendingMessage(false);
        if (response.error == 'Payment amount is too small to process.') {
          showSnackBar({ type: 'error', message: 'The amount requested is to low, please try with a higher amount.' });
          return;
        }
        showSnackBar({ type: 'error', message: 'Payment settings are not valid for ' + Conversation.locationName });
        return;
      }

      const paymentMessageBody: GravityPaymentMessageBody = {
        memo: memo,
        amount: amount,
        urlShortner: response.data.urlShortener,
        Id: response.data.externalTransId,
        paymentPageId: response.data.paymentPageId,
        invoiceId: selectedInvoice?.toString() ?? "",
        paymentStatus: MessagePaymentStatus.Pending,
        linkExpiryDate: response.data.linkExpiryDate,
        dealerId: Conversation.dealerId
      };
      const jsonRefBody = JSON.stringify(paymentMessageBody);

      let message = response.data.urlShortener;
      if (memo) {
        message = `${message} Memo: ${memo}`;
      }

      SendPayment(message, MessageTypeConst.PaymentSent, jsonRefBody, response.data.externalTransId);
      handleClose();
    }
    catch (err) {
      showSnackBar({ type: 'error', message: 'An error occurred while processing the payment message.' });
    }

    SetIsSendingMessage(false);
  }

  function CheckAmount(value: any) {
    if (isNaN(value)) return;
    if (value[0] === '0') return;
    if (value.toString().indexOf('.') > -1 && value.toString().split('.')[1].length > 2) return;
    setAmount(value);
  }
  const handleInvoiceChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const raw = event.target.value as number | "";
    const invoiceId = raw === "" ? undefined : (raw as number);

    setSelectedInvoice(invoiceId);

    const sel = gravityInvoices?.find(x => x.invoiceId === invoiceId);
    if (sel?.invoiceAmount) {
      setSelectedInvoiceAmount(sel.invoiceAmount);
    } else {
      setSelectedInvoiceAmount(null);
    }

    if (sel && Number(sel.invoiceAmount) > 0) {
      // preserve your existing CheckAmount logic
      CheckAmount((sel.invoiceAmount ?? 0) - (sel.payment ?? 0));
    } else {
      // if cleared, clear amount (optional)
      // setAmount('');
    }
  };


  const getAmountValidation = () => {
    const floatAmount = parseFloat(amount || '0');

    if (amount === '') {
      return { isError: true, message: '' };
    }

    if (floatAmount === 0) {
      return { isError: true, message: 'Amount cannot be $0' };
    }

    if (selectedInvoiceAmount && floatAmount > selectedInvoiceAmount) {
      return { isError: true, message: 'Amount cannot exceed invoice amount' };
    }

    return { isError: false, message: '' };
  };

  return (
    <>
      <Tooltip title="Text-To-Pay">
        <IconButton
          style={{ padding: 0, marginTop: ".2rem", marginLeft: "-.2rem" }}
          size="small"
          onClick={handleClick}
        >
          <AttachMoneyIcon />
        </IconButton>
      </Tooltip>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        {/* Header */}
        <Toolbar
          style={{
            backgroundColor: "#3f51b5",
            minHeight: 50,
            paddingLeft: 8,
            paddingRight: 8,
          }}
          variant="dense"
        >
          <Box style={{ flexGrow: 1, paddingLeft: 12 }}>
            <Typography
              variant="h4"
              style={{ color: "white", fontWeight: "bold" }}
            >
              Request Payment
            </Typography>
          </Box>
          <IconButton
            id="closeBtn"
            style={{ margin: "-8px", padding: "4px" }}
            onClick={handleClose}
          >
            <CloseIcon style={{ color: "white" }} />
          </IconButton>
        </Toolbar>

        <Box
          style={{
            width: "100%",
            maxWidth: 400,
            borderRadius: 8,
          }}
        >
          {/* Tabs */}
          {isTesting() && (
            <Tabs
              value={tab}
              onChange={handleTabChange}
              TabIndicatorProps={{ style: { backgroundColor: "#3f51b5" } }}
              textColor="primary"
              variant="fullWidth"
              style={{ minHeight: 36 }}
            >
              <Tab label="Balance" classes={{ root: classes.tabRoot }} />
              <Tab label="Deposit" classes={{ root: classes.tabRoot }} />
              <Tab label="Manual" classes={{ root: classes.tabRoot }} />
            </Tabs>
          )}

          {/* Content wrapper */}
          <Box
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: isTesting() ? "space-between" : "flex-start", // space-between for tabs, flex-start for no tabs
              minHeight: isTesting() ? 275 : undefined, // only reserve height if tabs exist
            }}
          >
            {/* Fields */}
            <Box style={{ padding: 16, paddingTop: 20 }}>
              <Grid container spacing={2}>
                {/* Memo Field */}
                <Grid item xs={12}>
                  <TextField
                    autoFocus
                    fullWidth
                    label="Memo"
                    size="small"
                    value={memo}
                    variant="outlined"
                    onChange={(e) => {
                      if (e.target.value.length <= MemoMessageBodyLimit) {
                        setMemo(e.target.value);
                      }
                    }}
                    helperText={`${memo.length}/${MemoMessageBodyLimit}`}
                    inputProps={{ maxLength: MemoMessageBodyLimit }}
                  />
                </Grid>

                {/* Invoice dropdown */}
                {isTesting() && tab !== 2 && (
                  <Grid item xs={12}>
                    <TextField
                      select
                      disabled={isLoading}
                      fullWidth
                      variant="outlined"
                      margin="dense"
                      size="small"
                      error={isNoInvoices}
                      helperText={isNoInvoices ? "No Order(s) found" : ""}
                      label="Select Order"
                      // <-- map undefined to empty string so the select shows the blank option
                      value={selectedInvoice ?? ""}
                      onChange={handleInvoiceChange}
                      InputProps={{
                        endAdornment: isLoading ? (
                          <div
                            style={{
                              position: "absolute",
                              right: 32,
                              top: "50%",
                              transform: "translateY(-50%)",
                            }}
                          >
                            <CircularProgress color="primary" size={20} />
                          </div>
                        ) : null,
                      }}
                    >
                      {/* blank placeholder option */}
                      <MenuItem value="">
                        <em>Select Order</em>
                      </MenuItem>

                      {gravityInvoices?.map((x: GravityPaymentInvoicesData) => (
                        <MenuItem key={x.invoiceId} value={x.invoiceId}>
                          {x.invoiceId} (${(x.invoiceAmount ?? 0) - (x.payment ?? 0)} •{" "}
                          {x.invoiceDate &&
                            moment.utc(x.invoiceDate).local().format("MM/DD/YYYY")}
                          )
                        </MenuItem>
                      ))}
                    </TextField>

                  </Grid>
                )}

                {/* Tab: Payment */}
                {tab === 0 && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Amount"
                      size="small"
                      value={amount}
                      variant="outlined"
                      onChange={(e) => !isTesting() && CheckAmount(e.target.value)}
                      disabled={isTesting()}
                      error={amount === ""}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">$</InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                )}

                {/* Tab: Deposit */}
                {tab === 1 && (
                  <>
                    <Grid item xs={12}>
                      <FormControl component="fieldset" fullWidth>
                        <RadioGroup
                          row
                          value={mode}
                          // <--- use the second parameter value (reliable)
                          onChange={(_e, value) => setMode(value as "amount" | "percentage")}
                        >
                          <FormControlLabel
                            value="amount"
                            control={<Radio color="primary" />}
                            label="Amount"
                          />
                          <FormControlLabel
                            value="percentage"
                            control={<Radio color="primary" />}
                            label="Percentage"
                          />
                        </RadioGroup>
                      </FormControl>
                    </Grid>

                    {mode === "percentage" && (
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="Percentage (%)"
                          size="small"
                          value={percentage ?? ""}
                          type="number"
                          variant="outlined"
                          onChange={(e) => handlePercentageChange(e.target.value)}
                          disabled={!selectedInvoice}
                          error={percentage == null}
                        />

                      </Grid>
                    )}

                    <Grid item xs={mode !== "percentage" ? 12 : 6}>
                      <TextField
                        fullWidth
                        label="Amount"
                        size="small"
                        value={amount}
                        variant="outlined"
                        disabled={!selectedInvoice || mode === "percentage"}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === "") {
                            setAmount("");
                            return;
                          }
                          const regex = /^\d+(\.\d{0,2})?$/;
                          if (regex.test(val)) setAmount(val);
                        }}
                        error={getAmountValidation().isError}
                        helperText={getAmountValidation().message}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">$</InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  </>
                )}

                {/* Tab: Take Cash */}
                {tab === 2 && (
                  <>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Amount"
                        size="small"
                        value={amount}
                        type="text" // change to text to control validation better
                        variant="outlined"
                        onChange={(e) => {
                          const val = e.target.value;

                          // Allow empty, or valid number with up to 2 decimals
                          if (val === "" || /^\d*\.?\d{0,2}$/.test(val)) {
                            setAmount(val);
                          }
                        }}
                        error={
                          amount !== "" &&
                          (parseFloat(amount) < 0.01 || parseFloat(amount) > 1000)
                        }
                        helperText={
                          amount === ""
                            ? "Amount is required"
                            : parseFloat(amount) < 0.01
                              ? "Minimum amount is $0.01"
                              : parseFloat(amount) > 1000
                                ? "Maximum amount is $1,000"
                                : ""
                        }
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">$</InputAdornment>
                          ),
                        }}
                      />


                    </Grid>

                    <Grid item xs={12}>
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        style={{ fontStyle: "italic", marginTop: 8 }}
                      >
                        Manual payments are not automatically posted to an order.
                      </Typography>
                    </Grid>
                  </>
                )}
              </Grid>
            </Box>
            {/* Submit Button footer */}
            <Box
              style={{
                padding: "16px",
                textAlign: "right",
                marginTop: isTesting() ? undefined : -24, // <-- reduce space only when no tabs
              }}
            >
              <Button
                color="primary"
                disabled={disableBtn()}
                variant="contained"
                style={{ minWidth: 180 }}
                onClick={SendPaymentRequest}
              >
                {!isSendingMessage
                  ? isTesting()
                    ? "Send"
                    : "Send Payment Request" // <-- change text when no tabs
                  : (
                    <>
                      Please wait...
                      <CircularProgress
                        style={{ marginLeft: 5 }}
                        color="inherit"
                        size={20}
                      />
                    </>
                  )}
              </Button>
            </Box>


          </Box>
        </Box>
      </Popover>
    </>
  );
}

export default TextToPay;
